import { useOutletContext, useParams } from "react-router";

import { useInfiniteQuery } from "@tanstack/react-query";

import { BugIcon, CheckIcon, CrownIcon, MailIcon, PlusIcon, ShieldIcon, TicketIcon, UsersIcon, XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { InvitationRow } from "@/features/members/components/InvitationRow";
import { InvitationRowSkeleton } from "@/features/members/components/InvitationRowSkeleton";
import InviteMemberForm from "@/features/members/components/InviteMemberForm";
import { MemberRow } from "@/features/members/components/MemberRow";
import { MemberRowSkeleton } from "@/features/members/components/MemberRowSkeleton";
import { PROJECT_ROLES, ROLE_CONFIG, ROLE_PERMISSIONS } from "@/features/members/config";
import { getProjectInvitations, getProjectMembers } from "@/features/projects/services/project";
import { Project } from "@/features/projects/types";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { StatCard, StatCardSkeleton } from "@/shared/components/StatCard";
import { useAuthentication } from "@/shared/hooks/useAuthentication";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

export default function ProjectMembers() {
  const { id } = useParams();
  const { project, permissions } = useOutletContext() as { project: Project; permissions: string[] };
  const { session } = useAuthentication();

  const canReadMembers = permissions.includes(PERMISSIONS.PROJECT_MEMBERS_READ);
  const canInviteMembers = permissions.includes(PERMISSIONS.PROJECT_INVITATIONS_CREATE);
  const canRemoveMembers = permissions.includes(PERMISSIONS.PROJECT_MEMBERS_DELETE);
  const canChangeRoles = permissions.includes(PERMISSIONS.PROJECT_MEMBERS_ROLE_UPDATE);
  const canLeaveProject = permissions.includes(PERMISSIONS.PROJECT_MEMBERS_LEAVE);
  const canReadInvitations = permissions.includes(PERMISSIONS.PROJECT_INVITATIONS_READ);
  const canRevokeInvitations = permissions.includes(PERMISSIONS.PROJECT_INVITATIONS_DELETE);

  const { data, isPending, error, refetch, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PROJECT_MEMBERS, id],
    queryFn: ({ pageParam }) => getProjectMembers(id!, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    enabled: canReadMembers,
  });

  const {
    data: invitationsData,
    isPending: invitationsPending,
    error: invitationsError,
    refetch: refetchInvitations,
    fetchNextPage: fetchNextInvitationsPage,
    isFetchingNextPage: isFetchingNextInvitationsPage,
    hasNextPage: hasNextInvitationsPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PROJECT_INVITATIONS, id],
    queryFn: ({ pageParam }) => getProjectInvitations(id!, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    enabled: canReadInvitations,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);
  const { open: invitationsErrorModalOpen, setOpen: setInvitationsErrorModalOpen, handleRetry: handleInvitationsRetry } = useErrorModal(invitationsError, refetchInvitations);

  const members = data?.pages.flatMap((page) => page.members) ?? [];
  const invitations = invitationsData?.pages.flatMap((page) => page.invitations) ?? [];
  const adminCount = members.filter((m) => m.role.name === "Admin").length;
  const devCount = members.filter((m) => m.role.name === "Developer").length;
  const totalTicketsClosed = members.reduce((s, m) => s + m.closedTicketCount, 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-normal uppercase">Members</h2>
          <p className="text-sm text-muted-foreground">Manage who has access to this project and their roles.</p>
        </div>
        {canInviteMembers ? (
          <InviteMemberForm project={project} />
        ) : (
          <PermissionTooltip allowed={false} message="You don't have permission to invite members to this project.">
            <Button size="sm" className="shrink-0">
              <PlusIcon />
              Invite member
            </Button>
          </PermissionTooltip>
        )}
      </div>

      {error && <Error variant="modal" title="Failed to load members" error={error as AppError | Error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {isPending ? (
          Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard label="Total members" value={project.totalMemberCount} sub={`${project.pendingInvitationsCount} invite${project.pendingInvitationsCount !== 1 ? "s" : ""} pending`} icon={UsersIcon} />
            <StatCard label="Admins" value={adminCount} sub={adminCount === 1 ? "1 admin role" : `${adminCount} admin roles`} icon={CrownIcon} />
            <StatCard label="Developers" value={devCount} sub="Can create & close tickets" icon={ShieldIcon} />
            <StatCard label="Tickets closed" value={totalTicketsClosed} sub="Across all members" icon={TicketIcon} />
          </>
        )}
      </div>

      {/* Members / invitations tabs */}
      <Card className="p-0 gap-0">
        <Tabs defaultValue="members">
          <CardHeader className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <TabsList className="h-full w-fit flex-row gap-1 bg-muted p-1">
                <TabsTrigger value="members" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <UsersIcon />
                  Members
                  {!isPending && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {project.totalMemberCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="invites" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <MailIcon />
                  Pending invites
                  {!isPending && project.pendingInvitationsCount > 0 && (
                    <Badge variant={null} className="ml-1 size-4 justify-center p-0 text-[10px]">
                      {project.pendingInvitationsCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>

          <TabsContent value="members" className="mt-0">
            {!canReadMembers ? (
              <EmptyState icon={BugIcon} title="No access" description="You don't have permission to view members." className="border border-dashed m-4" />
            ) : error && !errorModalOpen ? (
              <InlineError title="Failed to load members" description="Something went wrong while fetching members." onRetry={handleRetry} className="border border-dashed m-4" />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead className="w-32">Role</TableHead>
                      <TableHead className="w-48">Ticket activity</TableHead>
                      <TableHead className="w-28">Joined</TableHead>
                      <TableHead className="w-16" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isPending
                      ? Array.from({ length: 5 }).map((_, i) => <MemberRowSkeleton key={i} />)
                      : members.map((member) => <MemberRow key={member.id} member={member} project={project} isCurrentUser={member.userId === session?.id} canRemoveMembers={canRemoveMembers} canChangeRoles={canChangeRoles} canLeaveProject={canLeaveProject} />)}
                  </TableBody>
                </Table>
                {!isPending && (
                  <div className="border-t px-4 py-3 flex items-center justify-between gap-4">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{members.length}</span> of <span className="font-semibold text-foreground">{project.totalMemberCount}</span> members
                    </p>
                    {hasNextPage && (
                      <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                        {isFetchingNextPage ? "Loading..." : "Load more"}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="invites" className="mt-0">
            {invitationsError && <Error variant="modal" title="Failed to load invitations" error={invitationsError as AppError | Error} open={invitationsErrorModalOpen} onOpenChange={setInvitationsErrorModalOpen} onRetry={handleInvitationsRetry} />}
            {!canReadInvitations ? (
              <EmptyState icon={BugIcon} title="No access" description="You don't have permission to view invitations." className="border border-dashed m-4" />
            ) : invitationsError && !invitationsErrorModalOpen ? (
              <InlineError title="Failed to load invitations" description="Something went wrong while fetching invitations." onRetry={handleInvitationsRetry} className="border border-dashed m-4" />
            ) : !invitationsPending && invitations.length === 0 ? (
              <EmptyState icon={MailIcon} title="No pending invites" description="All sent invitations have been accepted." className="border border-dashed m-4" />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead className="w-32">Role</TableHead>
                      <TableHead className="w-40">Invited by</TableHead>
                      <TableHead className="w-36">Sent</TableHead>
                      <TableHead className="w-16" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitationsPending ? Array.from({ length: 3 }).map((_, i) => <InvitationRowSkeleton key={i} />) : invitations.map((invitation) => <InvitationRow key={invitation.id} invitation={invitation} projectId={id!} canRevokeInvitations={canRevokeInvitations} />)}
                  </TableBody>
                </Table>
                {!invitationsPending && (
                  <div className="border-t px-4 py-3 flex items-center justify-between gap-4">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{invitations.length}</span> pending {invitations.length === 1 ? "invitation" : "invitations"}
                    </p>
                    {hasNextInvitationsPage && (
                      <Button variant="outline" size="sm" onClick={() => fetchNextInvitationsPage()} disabled={isFetchingNextInvitationsPage}>
                        {isFetchingNextInvitationsPage ? "Loading..." : "Load more"}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Role reference card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <ShieldIcon className="size-4 text-primary" />
            <CardTitle>Role permissions</CardTitle>
          </div>
          <CardDescription>A summary of what each role can do within this project.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 sm:grid-cols-3">
            {PROJECT_ROLES.map((role) => {
              const cfg = ROLE_CONFIG[role];
              const Icon = cfg.icon;
              const rolePermissions = ROLE_PERMISSIONS[role];
              const roleCount = isPending ? null : members.filter((m) => m.role.name === role).length;

              return (
                <div key={role} className="flex flex-col gap-4 p-5">
                  <div className="flex items-center gap-2">
                    <Icon className={`size-4 ${cfg.className}`} />
                    <span className="text-sm font-semibold">{role}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{cfg.description}</p>
                  <ul className="flex flex-col gap-2">
                    {rolePermissions.map((p) => (
                      <li key={p.label} className="flex items-center gap-2 text-xs">
                        {p.allowed ? <CheckIcon className="size-3.5 shrink-0 text-primary" /> : <XIcon className="size-3.5 shrink-0 text-muted-foreground/40" />}
                        <span className={p.allowed ? "text-foreground" : "text-muted-foreground"}>{p.label}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                    <UsersIcon className="size-3 shrink-0" />
                    {roleCount === null ? <Skeleton className="h-3 w-16" /> : `${roleCount} member${roleCount !== 1 ? "s" : ""}`}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
